import { useEffect } from 'react';
import { create } from 'zustand';
import { api } from './axios';

// ─── Tenant-weites Stunden-Anzeigeformat ──────────────────────────────────────
// Die Admin-Einstellung `hoursFormat` (Backend: TenantTimeConfig.monthlyReportConfig)
// steuert die Darstellung von Dauern auf allen Seiten. Wird einmal via
// /time-config/clock-settings geladen und dann app-weit wiederverwendet.

export type HoursFormat = 'decimal' | 'hhmm';

/**
 * Formatiert Minuten als Stundenwert (ohne Einheit).
 *  - decimal: max. 2 Kommastellen, keine überflüssigen Nullen (8 · 8,5 · 8,25)
 *  - hhmm:    Stunden:Minuten (8:00 · 8:15)
 */
export function formatHours(minutes: number | null | undefined, format: HoursFormat): string {
  const min = Math.round(Number(minutes) || 0);
  const neg = min < 0;
  const abs = Math.abs(min);
  if (format === 'hhmm') {
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `${neg ? '-' : ''}${h}:${String(m).padStart(2, '0')}`;
  }
  const hours = abs / 60;
  const s = hours.toLocaleString(navigator.language, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  return `${neg ? '-' : ''}${s}`;
}

/**
 * Wie {@link formatHours}, hängt aber im Dezimalmodus die Einheit " h" an.
 * Im H:MM-Modus impliziert der Doppelpunkt bereits Stunden:Minuten.
 */
export function formatHoursWithUnit(minutes: number | null | undefined, format: HoursFormat): string {
  const v = formatHours(minutes, format);
  return format === 'decimal' ? `${v} h` : v;
}

interface HoursFormatState {
  hoursFormat: HoursFormat;
  loaded: boolean;
  load: () => Promise<void>;
}

export const useHoursFormatStore = create<HoursFormatState>((set, get) => ({
  hoursFormat: 'decimal',
  loaded: false,
  load: async () => {
    if (get().loaded) return;
    try {
      const res = await api.get('/time-config/clock-settings');
      const hf = res.data?.hoursFormat === 'hhmm' ? 'hhmm' : 'decimal';
      set({ hoursFormat: hf, loaded: true });
    } catch {
      set({ loaded: true });
    }
  },
}));

/** Hook: liefert das tenant-weite Stunden-Format (lädt es beim ersten Aufruf). */
export function useHoursFormat(): HoursFormat {
  const hoursFormat = useHoursFormatStore((s) => s.hoursFormat);
  const load = useHoursFormatStore((s) => s.load);
  useEffect(() => {
    void load();
  }, [load]);
  return hoursFormat;
}
